/**
 * Central hook for tab-visibility-aware state recovery.
 *
 * When the tab becomes visible after being hidden, this hook:
 * 1. Forces a server-time re-sync via REST
 * 2. Invalidates React Query caches for ongoing events and upcoming events
 *
 * The WS health check + VS_REJOIN is handled by realtimeClient.ts independently.
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { forceResync } from 'shared/queries/serverTime';

const MIN_REJOIN_INTERVAL_MS = 10_000;
const REJOIN_DEBOUNCE_MS = 500;

export function useTabRejoin(scheduleIds: string[]): void {
  const queryClient = useQueryClient();
  const lastRejoinRef = React.useRef(0);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const stableScheduleIdsKey = React.useMemo(
    () => [...scheduleIds].sort().join(','),
    [scheduleIds]
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        const now = Date.now();
        if (now - lastRejoinRef.current < MIN_REJOIN_INTERVAL_MS) return;
        lastRejoinRef.current = now;

        forceResync();

        const ids = stableScheduleIdsKey.split(',').filter(Boolean);
        for (let i = 0; i < ids.length; i++) {
          const sid = ids[i];
          if (i === 0) {
            queryClient.invalidateQueries({ queryKey: ['video', 'ongoingEvent', sid] });
            queryClient.invalidateQueries({ queryKey: ['events', 'scheduleUpcoming', sid] });
          } else {
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['video', 'ongoingEvent', sid] });
              queryClient.invalidateQueries({ queryKey: ['events', 'scheduleUpcoming', sid] });
            }, i * 200);
          }
        }
      }, REJOIN_DEBOUNCE_MS);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [queryClient, stableScheduleIdsKey]);
}
